import { isNonEmptyString, isNotNull } from '../../../util/check';
import { FN_INPUT_DEFAULT_FORMAT } from './../../../core/model/fn-form.constant';
import {
	Component,
	forwardRef,
	Input,
	OnInit,
	Output,
	EventEmitter,
	ViewEncapsulation,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FnInputValueAccessor } from '../../../core/model/fn-input.value.accessor';
import { FN_INPUT_TYPES } from '../../../core/index';

/**
 * This component is used to create input Field of different types .
 * To use this component import FnInputModule in Module.
 */

@Component({
	selector: 'fn-base-input',
	templateUrl: 'fn-base-input.component.html',
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => FnBaseInputComponent),
			multi: true,
		},
	],
})
export class FnBaseInputComponent
	extends FnInputValueAccessor<any>
	implements OnInit
{
	showPassword: boolean = false;
	/**
	 * member variable
	 * used to compare which input type to create like TEXT,EMAIL,PASSWORD etc.
	 */
	inputTypeMap = FN_INPUT_TYPES;

	/**
	 * Input member of string type
	 * used to set the name of input box
	 */
	@Input() name = '';
	/**
	 * Input member of string type
	 * used to set the id of input box;
	 */
	@Input() id = '';

	/**
	 * Input  member
	 * used to show Currency Format in input if given
	 */
	@Input() format: string;
	/**
	 * Input  member
	 * used to show the placeholder in input box
	 */
	@Input() placeholder = '';
	/**
	 * Input member variable
	 * used to align the text in input box or text area
	 * default value is left
	 * type ('left' | 'right' | 'center' | 'justify')
	 */
	@Input() textAlign: 'left' | 'right' | 'center' | 'justify' = 'left';
	/**
	 * Input member variable
	 * if true then show error box if input data is not valid.
	 */
	@Input() isErrorBox = true;
	/**
	 * Input  member variable
	 * use to validate min char can be inserted in input box
	 */
	@Input() minLength = 0;
	/**
	 * Input  member variable
	 * used to validate input field .
	 * if true then input box cannot be empty.
	 */
	@Input() isRequired = false;
	/**
	 * Input  member variable
	 * use to validate max char can be inserted in input box
	 */
	@Input() maxLength = 255;
	/**
	 * Input  member variable
	 * use to allow number before Decimal for Float Number
	 */
	@Input() beforeDecimal?: number;
	/**
	 * Input  member variable
	 * use to allow number after Decimal for Float Number
	 */
	@Input() afterDecimal?: number;
	/**
	 * Input  Member variable
	 * used to validate number if true then minus is not included in Length
	 */
	@Input() isNotIncludeMinus?: boolean;
	/**
	 * Input Member variable
	 * if true then allow negative number else allow only positive number.
	 */
	@Input() isNegative: boolean;
	/**
	 * Input member of string type
	 * Defines the type of input box to create
	 */
	@Input() type = 'TEXT';

	/**
	 * Input member
	 * used to pass the form object to show error message
	 * if validation is used in FormControl
	 */

	@Input() parentForm: any = null;
	/**
	 * Input member variable
	 * if true then then the text in input box cannot be changed
	 */
	@Input() readonly = false;
	/**
	 * Input member variable
	 * default value ='off'
	 * allow the browser to predict the value if autocomplete is on
	 */
	@Input() autocomplete = 'off';
	/**
	 * Input member
	 * default value = 0
	 * used to set the tabindex of input tag
	 */
	@Input() tabindex = 0;
	/**
	 * optional member of any type
	 * used to put data in input box which matches the given pattern
	 */

	@Input() pattern?: any;
	/**
	 * Input member.
	 * used to allow zero at the start of digit.
	 */
	@Input() preZero: boolean = false;
	/**
	 * Output member.
	 * used to emit event when input box is focused.
	 */
	@Output() focusEvent: EventEmitter<any> = new EventEmitter();
	/**
	 * Output  member
	 * used to emit event when input box losses focus.
	 */
	@Output() blurEvent: EventEmitter<any> = new EventEmitter();
	/**
	 * Output  member
	 * used to emit event when input is changed from child component
	 */
	@Output() changeEvent: EventEmitter<any> = new EventEmitter();
	/**
	 * Output  member
	 * used to emit event when input is changed from child component
	 */
	@Output() keyupEvent: EventEmitter<any> = new EventEmitter();
	/**
	 * Implements onInit event handler
	 * to call setOption method.
	 */
	ngOnInit() {
		this.setOption();
		if (this.format === undefined) {
			this.format = FN_INPUT_DEFAULT_FORMAT[this.type];
		}
	}

	/**
	 * Sets option funcion
	 * sets input box placeholder value.
	 */
	setOption() {
		this.placeholder = this.placeholder || this.name;
	}
	/**
	 * method to set value change from child Component or directive
	 */
	childChange(event: any) {
		this.changeEvent.emit(this.value);
	}

	handleKeyDown(event: KeyboardEvent) {
		if (event.keyCode === 32) {
			event.preventDefault();
			event.stopPropagation();
		}
	}

	restrictSpace(event: Event) {
		if (isNotNull(this.value) && isNonEmptyString(this.value)) {
			setTimeout(() => {
				this.value = this.value.replace(/\s/g, '');
				this.keyupEvent.emit(this.value);
			});
		}
	}
}
